/* global Marionette, _, define, jQuery, nfConversationalForms */
/* eslint-disable no-var, object-shorthand */
/**
 * The takeover.
 *
 * Everything about the form occupying the whole viewport lives here: entering
 * it, making the rest of the page inert so it is neither tabbable nor
 * announced, giving that page back, providing the exits — the close control,
 * the one the completion message has no way of providing itself, and the
 * confirmation that stands in front of discarding answers — and the cleanup
 * that has to happen even when the view is destroyed mid-flow.
 *
 * It is the one concern that acts almost entirely *outside* the view's own
 * element, which is why it is worth reading on its own: a maintainer changing
 * what the takeover reaches for should not have to read the render, step, and
 * theming code wrapped around it.
 *
 * Like the step and theme modules, this reaches into no view. It is given the
 * three things it needs — the view's element, the view's scoped lookup, and
 * the wrapper that bounds what may be made inert — plus a way to ask whether
 * anything has been entered that leaving would lose.
 */
define( 'controllers/takeover',[], function () {
	/**
	 * Wording of last resort for the discard confirmation.
	 *
	 * The localized string is what a visitor should see. This exists only so a
	 * site whose localized data predates that string gets a sentence instead of
	 * an empty confirmation dialog, which would give the visitor two unlabelled
	 * buttons and no idea what they decide.
	 */
	var discardFallback =
		'Leave without sending your answers? Anything you have entered will be discarded.';

	var controller = Marionette.Object.extend( {
		/**
		 * @param {Object}   options                  Controller options.
		 * @param {Object}   options.el               The view's own element.
		 * @param {Function} options.lookup           The view's scoped lookup.
		 * @param {Function} options.boundary         Returns the Ninja Forms
		 *                                            wrapper the takeover must
		 *                                            not make inert.
		 * @param {Function} options.hasUnsentAnswers Reports whether anything
		 *                                            has been entered that
		 *                                            leaving would discard.
		 * @return {void}
		 */
		initialize: function ( options ) {
			this.el = options.el;
			this.lookup = options.lookup;
			this.boundary = options.boundary;
			this.hasUnsentAnswers = options.hasUnsentAnswers;
			this.backgroundInerted = [];
		},

		/**
		 * Whether the form is currently taking over the viewport.
		 *
		 * @return {boolean} True while the takeover is open.
		 */
		isOpen: function () {
			return this.el.hasClass( 'nf-cf--full-screen' );
		},

		/**
		 * Take over the viewport with the conversational form.
		 *
		 * The body class is what stops the page behind from scrolling, and
		 * inerting the rest of the page is what stops the visitor from
		 * tabbing into it. Hiding something visually does not remove it from
		 * the tab order or from a screen reader, so a takeover without this
		 * leaves the theme's header and footer reachable behind the form —
		 * the same failure the step-level `hidden`/`inert`/`aria-hidden`
		 * treatment in the view's update() exists to prevent, one level up.
		 *
		 * @return {void}
		 */
		enter: function () {
			this.el.addClass( 'nf-cf--full-screen' );
			jQuery( 'body' ).addClass( 'nf-cf-full-screen-open' );
			this.inertBackground();
		},

		/**
		 * Leave the viewport, keeping the view alive.
		 *
		 * @return {void}
		 */
		close: function () {
			this.el.removeClass( 'nf-cf--full-screen' );
		},

		/**
		 * Finish an entry that had no page around it yet.
		 *
		 * A public form page enters the takeover during render, when there is
		 * nothing above the view to make inert, so the view calls this once it
		 * is in the document.
		 *
		 * @return {void}
		 */
		ensureBackgroundInert: function () {
			if ( this.isOpen() && ! this.backgroundInerted.length ) {
				this.inertBackground();
			}
		},

		/**
		 * Make everything outside the form's own wrapper inert.
		 *
		 * The boundary is the Ninja Forms wrapper rather than the view,
		 * because the success message is rendered as a sibling inside that
		 * wrapper and has to stay reachable — the completion screen is where
		 * the visitor lands after submitting in full screen.
		 *
		 * Walking up from the boundary and marking each ancestor's siblings
		 * covers the whole page without assuming any particular theme markup.
		 * Only elements this controller actually changed are recorded, and
		 * their previous `aria-hidden` value is kept, so restoring cannot
		 * clobber state that belonged to the page or to another add-on.
		 *
		 * @return {void}
		 */
		inertBackground: function () {
			var boundary = this.boundary();
			var body = document.body;
			var node = ( boundary.length ? boundary : this.el ).get( 0 );

			this.backgroundInerted = [];

			if ( ! node ) {
				return;
			}

			while ( node && node !== body && node.parentNode ) {
				jQuery( node )
					.siblings()
					.not( 'script, style, link, template' )
					.each(
						function ( index, sibling ) {
							if ( sibling.inert ) {
								return;
							}

							this.backgroundInerted.push( {
								el: sibling,
								ariaHidden:
									sibling.getAttribute( 'aria-hidden' ),
							} );

							sibling.inert = true;
							sibling.setAttribute( 'aria-hidden', 'true' );
						}.bind( this )
					);

				node = node.parentNode;
			}
		},

		/**
		 * Give the page behind the takeover back to the visitor.
		 *
		 * @return {void}
		 */
		restoreBackground: function () {
			_.each( this.backgroundInerted || [], function ( record ) {
				record.el.inert = false;

				if ( null === record.ariaHidden ) {
					record.el.removeAttribute( 'aria-hidden' );
					return;
				}

				record.el.setAttribute( 'aria-hidden', record.ariaHidden );
			} );

			this.backgroundInerted = [];
		},

		/**
		 * Leave the takeover, or leave the page when the form is the page.
		 *
		 * Both exits — the close control and the completion screen's own —
		 * share this prologue, and the background restore in particular must
		 * not be forgotten by either: whichever way the takeover ends, the
		 * page behind it has to become reachable again.
		 *
		 * On a dedicated form page there is nothing behind the takeover, so
		 * leaving it means leaving the page — and that discards every answer,
		 * since nothing is kept in the browser and Ninja Forms has no partial
		 * submission to come back to. That branch therefore asks first when
		 * there is something to lose. The completion exit guards nothing: by
		 * then the submission is stored and the page holds no unsent answer.
		 *
		 * @param {boolean} guardAnswers Whether unsent answers must be
		 *                               confirmed before the page is left.
		 * @return {boolean} True when the takeover has been dealt with here —
		 *                   the page is leaving, or the visitor chose to stay
		 *                   on it — and the caller must do nothing more.
		 */
		exit: function ( guardAnswers ) {
			if ( ! nfConversationalForms.publicForm ) {
				jQuery( 'body' ).removeClass( 'nf-cf-full-screen-open' );
				this.restoreBackground();

				return false;
			}

			if (
				guardAnswers &&
				this.hasUnsentAnswers() &&
				// The browser's own confirmation is used deliberately: it is
				// keyboard-operable and announced everywhere without this
				// add-on inventing a dialog of its own inside a takeover it
				// is about to leave.
				// eslint-disable-next-line no-alert
				! window.confirm(
					nfConversationalForms.i18n.confirmDiscard || discardFallback
				)
			) {
				return true;
			}

			window.location.assign( nfConversationalForms.homeUrl );

			return true;
		},

		/**
		 * Give the completion message the exit it cannot provide itself.
		 *
		 * Ninja Forms' success message knows nothing about the takeover, so in
		 * full screen it would be a panel with no way out. The close control
		 * added here is the takeover's own exit, wearing the completion
		 * screen's clothes.
		 *
		 * @param {Object} panel The success message panel.
		 * @return {void}
		 */
		decorateCompletion: function ( panel ) {
			if ( ! this.isOpen() ) {
				return;
			}

			var close = jQuery( '<button>', {
				type: 'button',
				class: 'nf-cf__close nf-cf__completion-close',
				'aria-label': nfConversationalForms.i18n.returnToSite,
				title: nfConversationalForms.i18n.returnToSite,
			} ).html( '<span aria-hidden="true">&times;</span>' );

			close.on( 'click', this.closeCompletion.bind( this ) );
			panel.prepend( close );
		},

		/**
		 * Dismiss the full-screen completion message.
		 *
		 * As with the close control, a dedicated form page has nothing to
		 * return to and goes home instead.
		 *
		 * Removing the control the visitor just activated would drop focus on
		 * to the document, and their next Tab would restart from the top of the
		 * page rather than beside the message still on screen. So focus is
		 * handed to whatever outlives the close: the launcher when the form is
		 * still on the page, and otherwise the completion message itself, which
		 * the view has already made focusable.
		 *
		 * @param {Object} event The click event on the close control.
		 * @return {void}
		 */
		closeCompletion: function ( event ) {
			if ( this.exit( false ) ) {
				return;
			}

			jQuery( event.currentTarget ).remove();

			var launcher = this.lookup( '.nf-cf__start' ).filter( ':visible' );

			if ( launcher.length ) {
				launcher.trigger( 'focus' );
				return;
			}

			// The panel is looked up again rather than held from the removed
			// control: a detached node has no ancestors left to climb.
			this.boundary().children( '.nf-response-msg' ).trigger( 'focus' );
		},

		/**
		 * Undo everything the takeover put on the page.
		 *
		 * The body class and the inert background live on elements that
		 * outlive the view. Leaving either behind is worse than untidy: the
		 * page would stay unscrollable and, without the background restore,
		 * permanently untabbable and unreadable to assistive technology, with
		 * no view left to undo it.
		 *
		 * @return {void}
		 */
		release: function () {
			jQuery( 'body' ).removeClass( 'nf-cf-full-screen-open' );
			this.restoreBackground();
		},
	} );

	return controller;
} );

